import {
  ActionIcon,
  Combobox,
  Group,
  InputBase,
  InputBaseProps,
  Loader,
  ScrollArea,
  Text,
  useCombobox,
} from "@mantine/core";
import { IconPlus, IconX } from "@tabler/icons-react";
import { FC, useCallback, useEffect, useMemo, useState } from "react";

export interface SelectOption {
  label: string;
  value: string;
}

interface CreatableSelectProps extends Omit<
  InputBaseProps,
  "value" | "onChange" | "onBlur"
> {
  data: SelectOption[];
  value?: string | null;
  onChange?: (value: string | null) => void;
  onBlur?: () => void;
  /**
   * Called when the user submits a new option.
   * Must return the created item ({ label, value }) or throw on failure.
   * Error handling (notifications) should be done inside this function.
   * Required when allowCreate is true (default).
   */
  onCreate?: (query: string) => Promise<SelectOption>;
  /** Set to false to disable the create-new-item option (search-only mode). Default: true */
  allowCreate?: boolean;
  clearable?: boolean;
  createLabel?: (query: string) => string;
  nothingFoundMessage?: string;
  placeholder?: string;
}

const CREATE_SENTINEL = "__CREATABLE_SELECT_CREATE__";

const CreatableSelect: FC<CreatableSelectProps> = ({
  data,
  value,
  onChange,
  onBlur,
  onCreate,
  allowCreate = true,
  clearable = true,
  createLabel = (q) => `Tạo mới "${q}"`,
  nothingFoundMessage = "Không tìm thấy kết quả",
  placeholder,
  disabled,
  ...inputProps
}) => {
  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
    },
  });

  const [search, setSearch] = useState("");
  const [createdItems, setCreatedItems] = useState<SelectOption[]>([]);
  const [isCreating, setIsCreating] = useState(false);

  const allOptions = useMemo(() => {
    const merged = [...data];
    for (const item of createdItems) {
      if (!merged.some((d) => d.value === item.value)) {
        merged.push(item);
      }
    }
    return merged;
  }, [data, createdItems]);

  const selectedLabel =
    allOptions.find((item) => item.value === value)?.label ?? "";

  // Sync display text when options load after mount (e.g. async data fetch)
  useEffect(() => {
    setSearch(selectedLabel);
  }, [selectedLabel]);

  const filteredOptions = useMemo(() => {
    if (!search) return allOptions;
    return allOptions.filter((item) =>
      item.label.toLowerCase().includes(search.toLowerCase()),
    );
  }, [allOptions, search]);

  const exactMatch = allOptions.some(
    (item) => item.label.toLowerCase() === search?.toLowerCase(),
  );
  const showCreate = allowCreate && search.trim().length > 0 && !exactMatch;

  const handleOptionSubmit = useCallback(
    async (val: string) => {
      if (val === CREATE_SENTINEL && onCreate) {
        setIsCreating(true);
        try {
          const newItem = await onCreate(search);
          setCreatedItems((prev) => [...prev, newItem]);
          onChange?.(newItem.value);
          setSearch(newItem.label);
        } finally {
          setIsCreating(false);
        }
      } else {
        const selected = allOptions.find((item) => item.value === val);
        onChange?.(val);
        setSearch(selected?.label ?? "");
      }
      combobox.closeDropdown();
    },
    [search, onCreate, onChange, allOptions, combobox],
  );

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onChange?.(null);
      setSearch("");
      combobox.closeDropdown();
    },
    [onChange, combobox],
  );

  const options = filteredOptions.map((item) => (
    <Combobox.Option key={item.value} value={item.value}>
      {item.label}
    </Combobox.Option>
  ));

  return (
    <Combobox store={combobox} onOptionSubmit={handleOptionSubmit}>
      <Combobox.Target>
        <InputBase
          {...inputProps}
          placeholder={placeholder}
          disabled={disabled || isCreating}
          rightSection={
            isCreating ? (
              <Loader size="xs" />
            ) : clearable && value ? (
              <ActionIcon
                size="xs"
                variant="transparent"
                color="gray"
                onMouseDown={handleClear}
                aria-label="Clear"
              >
                <IconX size={12} />
              </ActionIcon>
            ) : (
              <Combobox.Chevron />
            )
          }
          rightSectionPointerEvents={clearable && value ? "all" : "none"}
          value={search}
          onChange={(e) => {
            setSearch(e.currentTarget.value);
            combobox.openDropdown();
            combobox.updateSelectedOptionIndex();
          }}
          onClick={() => combobox.openDropdown()}
          onFocus={() => combobox.openDropdown()}
          onBlur={() => {
            combobox.closeDropdown();
            setSearch(selectedLabel);
            onBlur?.();
          }}
        />
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Options>
          <ScrollArea.Autosize mah={220} type="scroll">
            {options.length === 0 && !showCreate && (
              <Combobox.Empty>{nothingFoundMessage}</Combobox.Empty>
            )}
            {options}
            {showCreate && (
              <Combobox.Option value={CREATE_SENTINEL}>
                <Group gap={6}>
                  <IconPlus size={14} />
                  <Text size="sm">{createLabel(search)}</Text>
                </Group>
              </Combobox.Option>
            )}
          </ScrollArea.Autosize>
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
};

export default CreatableSelect;
